import { useCallback } from "react";
import { ConditionGroup } from "stores/data/insightAlerts/insightAlertModel";
import { IEnvironmentalVariable } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import {
  createDefaultConditionGroup,
  createDefaultCondition,
  reorganizeGroupParentIds,
  canRemoveGroup,
  canRemoveCondition,
} from "../utils/conditionUtils";

interface UseConditionGroupsProps {
  conditionGroups: ConditionGroup[];
  onConditionGroupsChange: (field: string, value: any) => void;
  environmentalVariables?: IEnvironmentalVariable[];
  defaultEnvironmentalVariableId?: number;
  /** Formik field name for the condition groups array. Defaults to "conditionGroups". */
  fieldName?: string;
}

/**
 * Hook para gerenciar grupos de condições e suas operações
 */
export const useConditionGroups = ({
  conditionGroups,
  onConditionGroupsChange,
  environmentalVariables,
  defaultEnvironmentalVariableId = 7,
  fieldName = "conditionGroups",
}: UseConditionGroupsProps) => {
  /** Resolve the default variable name from the environmentalVariables list */
  const defaultEnvironmentalVariable = environmentalVariables?.find(
    (v) => v.id === defaultEnvironmentalVariableId
  )?.internal_name;

  /**
   * Adiciona uma nova condição a um grupo específico
   */
  const addConditionToGroup = useCallback(
    (groupIndex: number) => {
      const updatedGroups = conditionGroups.map((group, i) => {
        if (i !== groupIndex) return group;
        const newCondition = defaultEnvironmentalVariable
          ? createDefaultCondition(defaultEnvironmentalVariableId, defaultEnvironmentalVariable)
          : createDefaultCondition(defaultEnvironmentalVariableId);
        return { ...group, conditions: [...group.conditions, newCondition] };
      });

      onConditionGroupsChange(fieldName, updatedGroups);
    },
    [conditionGroups, onConditionGroupsChange, defaultEnvironmentalVariableId, defaultEnvironmentalVariable, fieldName]
  );

  /**
   * Remove uma condição de um grupo específico
   */
  const removeConditionFromGroup = useCallback(
    (groupIndex: number, conditionIndex: number) => {
      const group = conditionGroups[groupIndex];

      if (!canRemoveCondition(group.conditions, conditionIndex)) {
        return;
      }

      const updatedGroups = conditionGroups.map((g, i) => {
        if (i !== groupIndex) return g;
        return {
          ...g,
          conditions: g.conditions.filter((_, ci) => ci !== conditionIndex),
        };
      });
      onConditionGroupsChange(fieldName, updatedGroups);
    },
    [conditionGroups, onConditionGroupsChange, fieldName]
  );

  /**
   * Adiciona um novo grupo de condições
   */
  const addNewGroup = useCallback(() => {
    const lastGroupId =
      conditionGroups.length > 0
        ? conditionGroups[conditionGroups.length - 1].id
        : null;

    const newGroup = defaultEnvironmentalVariable
      ? createDefaultConditionGroup(lastGroupId, defaultEnvironmentalVariableId, defaultEnvironmentalVariable)
      : createDefaultConditionGroup(lastGroupId, defaultEnvironmentalVariableId);

    onConditionGroupsChange(fieldName, [...conditionGroups, newGroup]);
  }, [
    conditionGroups,
    onConditionGroupsChange,
    defaultEnvironmentalVariableId,
    defaultEnvironmentalVariable,
    fieldName,
  ]);

  /**
   * Remove um grupo de condições
   */
  const removeGroup = useCallback(
    (groupIndex: number) => {
      if (!canRemoveGroup(conditionGroups, groupIndex)) {
        return;
      }

      const updatedGroups = [...conditionGroups];
      updatedGroups.splice(groupIndex, 1);

      const reorganizedGroups = reorganizeGroupParentIds(
        updatedGroups,
        groupIndex
      );
      onConditionGroupsChange(fieldName, reorganizedGroups);
    },
    [conditionGroups, onConditionGroupsChange, fieldName]
  );

  /**
   * Atualiza o operador de grupo (AND/OR entre grupos)
   */
  const updateGroupOperator = useCallback(
    (groupIndex: number, operator: string) => {
      const updatedGroups = [...conditionGroups];
      updatedGroups[groupIndex].groupOperator = operator;
      onConditionGroupsChange(fieldName, updatedGroups);
    },
    [conditionGroups, onConditionGroupsChange, fieldName]
  );

  /**
   * Atualiza o operador de condição (AND/OR entre condições dentro do grupo)
   */
  const updateConditionOperator = useCallback(
    (groupIndex: number, operator: string) => {
      const updatedGroups = [...conditionGroups];
      updatedGroups[groupIndex].conditionOperator = operator;
      onConditionGroupsChange(fieldName, updatedGroups);
    },
    [conditionGroups, onConditionGroupsChange, fieldName]
  );

  /**
   * Atualiza uma condição específica
   */
  const updateCondition = useCallback(
    (groupIndex: number, conditionIndex: number, field: string, value: any) => {
      const updatedGroups = [...conditionGroups];
      const condition = updatedGroups[groupIndex].conditions[conditionIndex];

      if (field === "environmentalVariableId") {
        const variable = environmentalVariables?.find(
          (x) => x.id === parseInt(value)
        );

        if (variable) {
          condition.environmentalVariableId = parseInt(value);
          condition.environmentalVariable = variable.internal_name;
        }
      } else {
        (condition as any)[field] = value;
      }

      onConditionGroupsChange(fieldName, updatedGroups);
    },
    [conditionGroups, onConditionGroupsChange, environmentalVariables, fieldName]
  );

  return {
    addConditionToGroup,
    removeConditionFromGroup,
    addNewGroup,
    removeGroup,
    updateGroupOperator,
    updateConditionOperator,
    updateCondition,
  };
};
